import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Dashboard } from '../shared/api';
import {
  ErrorNote, Empty, RiskPill, Skeleton, StatePill, formatDate, titleise,
} from '../shared/ui';
import { useAuth } from '../app/auth';

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Dashboard>('/dashboard'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Skeleton rows={7} />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const { kpis, cash_position, recent, top_risks, payroll } = data;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Treasury</div>
          <h1 className="page-title">
            {greeting()}, {user?.display_name.split(' ')[0]}
          </h1>
          <p className="page-sub">
            {kpis.pending_my_approval > 0
              ? `${kpis.pending_my_approval} payment order${kpis.pending_my_approval === 1 ? '' : 's'} needs your approval.`
              : 'Nothing is waiting on you right now.'}
          </p>
        </div>
        {kpis.pending_my_approval > 0 && (
          <Link className="btn btn-primary" to="/approvals">
            Review approvals
          </Link>
        )}
      </div>

      <div className="grid grid-kpi" style={{ marginBottom: 16 }}>
        <Stat label="Payment orders" value={String(kpis.total_orders)} note="All time" />
        <Stat
          label="Awaiting you"
          value={String(kpis.pending_my_approval)}
          note="At your level"
          tone={kpis.pending_my_approval > 0 ? 'var(--seal)' : undefined}
        />
        <Stat label="Committed out" value={kpis.total_debit.formatted} note="Approved and beyond" tone="var(--breach)" />
        <Stat label="Committed in" value={kpis.total_credit.formatted} note="Approved and beyond" tone="var(--verify)" />
        <Stat
          label="Open findings"
          value={String(kpis.open_risk_findings)}
          note="Raised by the risk agent"
          tone={kpis.open_risk_findings > 0 ? 'var(--watch)' : undefined}
        />
      </div>

      {payroll && (
        <div className="grid grid-kpi" style={{ marginBottom: 16 }}>
          <Stat
            label="Active employees"
            value={String(payroll.active_employees)}
            note="On payroll"
          />
          <Stat
            label="Latest pay run"
            value={payroll.latest_run?.label ?? 'None'}
            note={payroll.latest_run ? titleise(payroll.latest_run.state) : 'No runs yet'}
            tone={payroll.latest_run?.state === 'DRAFT' ? 'var(--watch)' : payroll.latest_run?.state === 'PAID' ? 'var(--verify)' : undefined}
          />
          <Stat
            label="Net payroll"
            value={payroll.latest_run?.total_net?.formatted ?? '—'}
            note={payroll.latest_run ? `${payroll.latest_run.employee_count} employees` : 'No data'}
          />
        </div>
      )}

      <div className="split">
        <div className="col gap-14">
          <div className="card card-flush">
            <div className="card-head" style={{ padding: '18px 18px 0' }}>
              <div className="card-title">Recent payment orders</div>
              <Link className="btn btn-ghost btn-sm" to="/orders">View all</Link>
            </div>
            {recent.length === 0 ? (
              <Empty
                title="No payment orders yet"
                hint="Create your first order to start the approval workflow."
                action={<Link className="btn btn-primary" to="/orders?new=1">Create a payment order</Link>}
              />
            ) : (
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Voucher</th>
                      <th className="hide-mobile">Purpose</th>
                      <th className="right">Amount</th>
                      <th>State</th>
                      <th className="hide-mobile">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((o) => (
                      <tr key={o.id} className="clickable">
                        <td>
                          <Link to={`/orders/${o.id}`} className="vno" style={{ textDecoration: 'none' }}>
                            {o.voucher_no}
                          </Link>
                          <div className="ghost" style={{ fontSize: 11 }}>{formatDate(o.value_date)}</div>
                        </td>
                        <td className="hide-mobile">{titleise(o.purpose)}</td>
                        <td className={`num ${o.txn_type === 'DEBIT' ? 'debit' : 'credit'}`}>
                          {o.total.formatted}
                        </td>
                        <td><StatePill state={o.state} /></td>
                        <td className="hide-mobile">{o.risk && <RiskPill level={o.risk.level} count={o.risk.count} />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Projected cash position</div>
              <span className="ghost" style={{ fontSize: 11 }}>Approved and published commitments</span>
            </div>
            <div className="col gap-10">
              {cash_position.map((a) => {
                const ratio =
                  a.opening.minor > 0
                    ? Math.max(0, Math.min(100, (a.projected.minor / a.opening.minor) * 100))
                    : 0;
                return (
                  <div key={a.account_id}>
                    <div className="row between" style={{ marginBottom: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{a.label}</span>
                      <span
                        className="mono"
                        style={{
                          fontSize: 13, fontWeight: 700,
                          color: a.is_negative ? 'var(--breach)' : 'var(--paper)',
                        }}
                      >
                        {a.projected.formatted}
                      </span>
                    </div>
                    <div
                      style={{ height: 6, background: 'var(--ink)', borderRadius: 4, overflow: 'hidden' }}
                      role="img"
                      aria-label={`${a.label}: ${a.projected.formatted} projected from ${a.opening.formatted}`}
                    >
                      <div
                        style={{
                          width: `${ratio}%`, height: '100%',
                          background: a.is_negative ? 'var(--breach)' : 'var(--verify)',
                          transition: 'width .5s cubic-bezier(.2,.8,.3,1)',
                        }}
                      />
                    </div>
                    <div className="row between ghost" style={{ fontSize: 11, marginTop: 4 }}>
                      <span>Opening {a.opening.formatted}</span>
                      <span>Out {a.committed_out.formatted}</span>
                    </div>
                    {a.is_negative && (
                      <div className="alert alert-bad mt-6" style={{ fontSize: 12 }}>
                        Commitments exceed the balance on this account.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">What the risk agent found</div>
            <Link className="btn btn-ghost btn-sm" to="/findings">All</Link>
          </div>
          {top_risks.length === 0 ? (
            <div className="alert alert-ok">
              Nothing flagged. The agent checks every order on submission for duplicates,
              threshold avoidance and recent bank-detail changes.
            </div>
          ) : (
            <div className="col gap-10">
              {top_risks.map((f) => (
                <div key={f.id} className={`finding ${f.risk}`}>
                  <div className="finding-title">
                    <RiskPill level={f.risk} />
                    {f.title}
                  </div>
                  <div className="finding-detail">{f.detail}</div>
                  <div className="ghost mono" style={{ fontSize: 10.5, marginTop: 7 }}>
                    {f.code} · {f.confidence}% confidence
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Stat({
  label, value, note, tone,
}: { label: string; value: string; note: string; tone?: string }) {
  return (
    <div className="card">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: tone, fontSize: value.length > 11 ? 19 : undefined }}>
        {value}
      </div>
      <div className="stat-note">{note}</div>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
